import React, { useState } from 'react';
import { Button, Separator, cn } from '@kiiwi/ui';
import { useTranslation, Trans } from '@/src/i18n';
import TLCSpecialityFilter from './TLCSpecialityFilter';
import TLCFeeFilter from './TLCFeeFilter';
import TLCPatientLimitationSwitch from './TLCPatientLimitationSwitch';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/not-existing-kiiwi-components/Dialog';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';

type Props = {
  filters: Record<string, any>;
  numberOfSearchResults: number;
  hasFiltersSelected: boolean;
  numberOfFiltersSelected: number;
  selectFilterValue: (filter: Record<string, any>) => void;
  resetAll: () => void;
};

const TLCMobileFilterBar = ({
  filters,
  numberOfSearchResults,
  hasFiltersSelected,
  numberOfFiltersSelected,
  selectFilterValue,
  resetAll,
}: Props) => {
  const { t } = useTranslation();
  const [areMobileFiltersOpened, setAreMobileFiltersOpened] = useState<boolean>(
    false,
  );

  const showMobileFilters = () => setAreMobileFiltersOpened(true);
  const hideMobileFilters = () => setAreMobileFiltersOpened(false);

  const onReset = (event: React.MouseEvent<SVGElement>) => {
    event.stopPropagation();
    resetAll();
  };

  return (
    <>
      <div className="fixed top-14 z-[49] flex w-full flex-col bg-surface-white px-4">
        <div className="flex items-center pb-6 pt-8">
          <TLCPatientLimitationSwitch
            filters={filters}
            selectValue={selectFilterValue}
          />
        </div>
        <Separator />
      </div>
      {!areMobileFiltersOpened && (
        <div className="fixed bottom-8 z-[1350] flex w-full justify-center rounded-t-lg">
          <div className="relative">
            <Button
              variant="secondary"
              size="lg"
              className={cn(
                'mx-auto flex w-[148px] flex-row gap-2 bg-surface-white font-bold',
                hasFiltersSelected && 'bg-surface-light-brand',
              )}
              onClick={showMobileFilters}
            >
              {`${t('filters')}${
                hasFiltersSelected ? ` (${numberOfFiltersSelected})` : ''
              }`}
              {hasFiltersSelected ? (
                <FontAwesomeIcon
                  name="xmark:regular"
                  className="size-6"
                  onClick={onReset}
                />
              ) : (
                <FontAwesomeIcon
                  name="chevron-down:regular"
                  className="size-6"
                />
              )}
            </Button>
          </div>
        </div>
      )}
      <Dialog open={areMobileFiltersOpened} onOpenChange={hideMobileFilters}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('filters')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <TLCSpecialityFilter
                filters={filters}
                selectValue={selectFilterValue}
              />

              <TLCFeeFilter filters={filters} selectValue={selectFilterValue} />
            </div>

            <TLCPatientLimitationSwitch
              filters={filters}
              selectValue={selectFilterValue}
            />
          </DialogBody>
          <DialogFooter className="pb-8">
            <Button
              disabled={!hasFiltersSelected}
              variant="ghost"
              size="md"
              className="mx-auto"
              onClick={resetAll}
            >
              {`${t('reset')}${
                numberOfFiltersSelected > 0
                  ? ` (${numberOfFiltersSelected})`
                  : ''
              }`}
            </Button>
            <Button
              variant="primary"
              size="md"
              className="mx-auto"
              onClick={hideMobileFilters}
            >
              <Trans
                i18nKey="display-results"
                values={{
                  numberOfResults: numberOfSearchResults,
                }}
                components={[
                  <span
                    className={cn(numberOfSearchResults < 2 && 'hidden')}
                    key="0"
                  />,
                ]}
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(TLCMobileFilterBar);
